import React from 'react';
import { useSchoolYear } from '../contexts/SchoolYearContext';
import { SchoolYearCard, SchoolYearStatusChip, schoolYearFieldStyle } from './SchoolYearCard';

const LABELS = {
  schoolYearSystemTitle: {
    fr: 'Consulter une année',
    ar: 'عرض سنة دراسية',
    en: 'View a school year',
  },
  schoolYearSystemHint: {
    fr: 'Change uniquement les données affichées, sans toucher à l\'année courante.',
    ar: 'يغيّر البيانات المعروضة فقط، دون تعديل السنة الحالية.',
    en: 'Changes the displayed data only, without touching the current year.',
  },
  schoolYearSelectLabel: {
    fr: 'Année consultée',
    ar: 'السنة المعروضة',
    en: 'Viewed year',
  },
  current: { fr: 'courante', ar: 'حالية', en: 'current' },
  closed: { fr: 'clôturée', ar: 'مغلقة', en: 'closed' },
  readOnly: { fr: 'Lecture seule', ar: 'قراءة فقط', en: 'Read only' },
} as const;

type LabelKey = keyof typeof LABELS;

interface SchoolYearSystemInfoProps {
  t?: (key: string) => string;
  isRTL?: boolean;
  lang?: 'fr' | 'en' | 'ar';
}

export const SchoolYearSystemInfo: React.FC<SchoolYearSystemInfoProps> = ({ t, isRTL = false, lang }) => {
  const { years, selectedYear, loading, isReadOnly, selectSchoolYear } = useSchoolYear();

  const resolvedLang: 'fr' | 'en' | 'ar' = lang ?? (isRTL ? 'ar' : 'fr');

  const lbl = (key: LabelKey) => {
    if (t) {
      const fromT = t(key);
      if (fromT !== key) return fromT;
    }
    return LABELS[key][resolvedLang];
  };

  return (
    <SchoolYearCard
      icon="📅"
      iconBg="#e0e7ff"
      titleColor="#3730a3"
      title={lbl('schoolYearSystemTitle')}
      hint={lbl('schoolYearSystemHint')}
      isRTL={isRTL}
      fieldId="school-year-consult"
      fieldLabel={lbl('schoolYearSelectLabel')}
      field={
        <select
          id="school-year-consult"
          value={selectedYear?.id ?? ''}
          disabled={loading || years.length === 0}
          onChange={(e) => {
            const y = years.find((yr) => String(yr.id) === e.target.value);
            if (y) selectSchoolYear(y);
          }}
          style={{
            ...schoolYearFieldStyle,
            cursor: loading ? 'wait' : 'pointer',
            backgroundColor: loading ? '#f9fafb' : '#fff',
          }}
        >
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.label}
              {y.is_current ? ` (${lbl('current')})` : ''}
              {y.status === 'closed' ? ` — ${lbl('closed')}` : ''}
            </option>
          ))}
        </select>
      }
      actions={
        isReadOnly ? <SchoolYearStatusChip label={lbl('readOnly')} tone="amber" /> : undefined
      }
    />
  );
};